import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { PRIORITY_COLORS } from '@/client/lib/colors'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Target,
  TrendingDown,
  MousePointerClick,
  Wrench,
  Users,
  ChevronDown,
  ChevronRight,
  Sparkles,
  ExternalLink,
} from 'lucide-react'
import { useInsights, type InsightCategory, type InsightItem } from '../hooks/useInsights'

const ICON_MAP: Record<string, React.ElementType> = {
  'target': Target,
  'trending-down': TrendingDown,
  'mouse-pointer-click': MousePointerClick,
  'wrench': Wrench,
  'users': Users,
}

// PRIORITY_COLORS imported from @/client/lib/colors

function InsightCategoryCard({ category }: { category: InsightCategory }) {
  const [open, setOpen] = useState(true)
  const Icon = ICON_MAP[category.icon] ?? Target

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Icon className="h-4 w-4 text-primary" />
                {category.label}
                <Badge variant="secondary" className="text-xs ml-1">
                  {category.count}
                </Badge>
              </CardTitle>
              {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
            </div>
            <p className="text-xs text-muted-foreground text-left">{category.description}</p>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="divide-y">
              {category.items.map((item, i) => (
                <InsightItemRow key={i} item={item} />
              ))}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

function InsightItemRow({ item }: { item: InsightItem }) {
  return (
    <div className="py-3 first:pt-1">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">{item.title}</span>
            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 shrink-0 ${PRIORITY_COLORS[item.priority]}`}>
              {item.priority}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.description}</p>
          <p className="text-xs text-primary/80 mt-1">{item.action}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-muted-foreground">{item.metric}</p>
          <p className="text-sm font-semibold">{item.metricValue}</p>
        </div>
      </div>
      {item.url && (
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-1 mt-1"
        >
          <ExternalLink className="h-2.5 w-2.5" />
          {item.url.replace(/^https?:\/\/[^/]+/, '')}
        </a>
      )}
    </div>
  )
}

export function InsightsTab({ siteId }: { siteId: string }) {
  const { data, isLoading } = useInsights(siteId)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  const categories = data?.insights ?? []

  if (categories.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Sparkles className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No insights yet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Insights are generated from GSC data, rank tracking, and crawl results.
            Connect Google Search Console or add tracked keywords to see recommendations.
          </p>
        </CardContent>
      </Card>
    )
  }

  const totalItems = categories.reduce((sum, c) => sum + c.count, 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {totalItems} actionable insight{totalItems !== 1 ? 's' : ''} across {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}
        </p>
        {data?.computedAt && (
          <p className="text-xs text-muted-foreground">
            Updated {new Date(data.computedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </p>
        )}
      </div>

      {categories.map((category) => (
        <InsightCategoryCard key={category.id} category={category} />
      ))}
    </div>
  )
}
